import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import ReactDOM from "react-dom";
import "../../CSS/Outscan.css";
import api from "../../../src/api.js";
import { useRights } from "../RightsContext.jsx"; 


const Outscan_PATH = "/Manifest_Outscan"; 
// ════════════════════════════════════════════
// CONSTANTS
// ════════════════════════════════════════════

// Type char(1) stored in DB  ↔  UI select value
const TYPE_CODE  = { regular: "R", express: "E", od: "O" };
const TYPE_UI    = { R: "regular", E: "express", O: "od" };
const TYPE_LABEL = { R: "Regular", E: "Express", O: "ODA/Special" };

const STATUS_LABEL = { O: "Open", C: "Closed", D: "Draft" };

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

// ════════════════════════════════════════════
// AUTOCOMPLETE / COMBOBOX — matches RateDefinition.jsx style
// ════════════════════════════════════════════
function ComboBox({ id, value, onChange, options = [], placeholder = "Search…", className = "", disabled = false, valueKey = "value", labelKey = "label" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focIdx, setFocIdx] = useState(-1);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const dropListRef = useRef(null);

  useEffect(() => {
    if (!open || focIdx < 0 || !dropListRef.current) return;
    const el = dropListRef.current.querySelector(`[data-idx="${focIdx}"]`);
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [focIdx, open]);

  // Normalise to { value, label } — case-insensitive key lookup
  const opts = useMemo(() => options.map((o) => {
    const keys = Object.keys(o);
    const vKey = keys.find((k) => k.toLowerCase() === valueKey.toLowerCase()) || valueKey;
    const lKey = keys.find((k) => k.toLowerCase() === labelKey.toLowerCase()) || labelKey;
    return {
      value: String(o[vKey] ?? o.value ?? o.id ?? ""),
      label: String(o[lKey] ?? o.label ?? o.name ?? ""),
    };
  }), [options, valueKey, labelKey]);

  const selected = opts.find((o) => o.value === String(value ?? ""));

  const highlight = (text, q) => {
    if (!q.trim()) return text;
    const idx = text.toLowerCase().indexOf(q.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark>{text.slice(idx, idx + q.length)}</mark>
        {text.slice(idx + q.length)}
      </>
    );
  };

  const filtered = query.trim()
    ? opts.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : opts;

  useEffect(() => {
    const h = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) closeBox();
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const openBox = () => {
    if (disabled) return;
    setOpen(true);
    setQuery("");
    setFocIdx(-1);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const closeBox = () => {
    setOpen(false);
    setQuery("");
    setFocIdx(-1);
  };

  const pick = (val) => {
    onChange({ target: { value: val } });
    closeBox();
  };

  const handleKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setFocIdx((i) => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setFocIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); if (focIdx >= 0 && filtered[focIdx]) pick(filtered[focIdx].value); else if (filtered.length === 1) pick(filtered[0].value); }
    else if (e.key === "Escape") closeBox();
  };

  const [dropStyle, setDropStyle] = useState({});
  const triggerRef = useRef(null);

  const updateDropPos = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    setDropStyle({ position: "fixed", top: r.bottom + 4, left: r.left, width: r.width, zIndex: 99999 });
  }, []);

  useEffect(() => {
    if (open) {
      updateDropPos();
      window.addEventListener("scroll", updateDropPos, true);
      window.addEventListener("resize", updateDropPos);
    }
    return () => {
      window.removeEventListener("scroll", updateDropPos, true);
      window.removeEventListener("resize", updateDropPos);
    };
  }, [open, updateDropPos]);

  return (
    <div className="cb-ac" ref={wrapRef} id={id}>
      <div ref={triggerRef} className={`cb-ac-box${open ? " open" : ""}${disabled ? " cb-disabled" : ""}`} onClick={openBox}>
        <input
          ref={inputRef}
          className={`cb-ac-in${className ? " " + className : ""}`}
          type="text"
          placeholder={selected ? selected.label : placeholder}
          value={open ? query : selected ? selected.label : ""}
          onChange={(e) => { setQuery(e.target.value); setFocIdx(-1); }}
          onKeyDown={handleKey}
          readOnly={!open}
          disabled={disabled}
          style={{ cursor: disabled ? "not-allowed" : open ? "text" : "pointer" }}
        />
        {selected && !open && (
          <span className="cb-ac-x on" onMouseDown={(e) => { e.stopPropagation(); pick(""); }}>×</span>
        )}
        <span className="cb-ac-chev">▾</span>
      </div>

      {open && ReactDOM.createPortal(
        <div className="cb-ac-drop open" style={dropStyle} ref={dropListRef}>
          {filtered.length === 0
            ? <div className="cb-ac-empty">No results found</div>
            : filtered.map((o, i) => (
              <div
                key={o.value}
                data-idx={i}
                className={`cb-ac-opt${o.value === String(value ?? "") ? " sel" : ""}${i === focIdx ? " foc" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); pick(o.value); }}
              >
                {highlight(o.label, query)}
              </div>
            ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// PlaceSelect — combobox alias for place_id / place_Name dropdown data
function PlaceSelect({ id, value, onChange, options, placeholder, className, disabled = false }) {
  return (
    <ComboBox
      id={id}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder || "Search place…"}
      className={className}
      disabled={disabled}
      valueKey="place_id"
      labelKey="place_Name"
    />
  );
}

// ════════════════════════════════════════════
// TOAST
// ════════════════════════════════════════════
function ToastContainer({ toasts }) {
  const icons = { ok: "✓", err: "✕", warn: "⚠", info: "ℹ" };
  return (
    <div className="toast-wrap">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={`toast ${t.type}`}
          style={t.fading ? { opacity: 0, transform: "translateX(40px)" } : {}}
        >
          <span style={{ fontSize: 15 }}>{icons[t.type] || "ℹ"}</span>
          {t.msg}
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════
export default function Manifest_Outscan() {
  const rights = useRights(Outscan_PATH);

  // ── view: "list" | "manifest"
  const [view, setView] = useState("list");

  // ── place dropdown (loaded once from API)
  const [places, setPlaces] = useState([]);   // [{ place_id, place_Name }]

  // ── list state
  const [filteredList, setFilteredList] = useState([]);
  const [listLoading,  setListLoading]  = useState(false);

  // ── pagination (Manifest Register)
 const [listPage,     setListPage]     = useState(1);
const listPageSize = 10;

  // ── filter state
  const [fBranch, setFBranch] = useState("");
  const [fType,   setFType]   = useState("");
  const [fFrom,   setFFrom]   = useState(todayISO().slice(0, 7) + "-01");
  const [fTo,     setFTo]     = useState(todayISO());
  const [fMnfNo,  setFMnfNo]  = useState("");

  // ── manifest master form
  const [manifestId,   setManifestId]   = useState(0);   // 0 = new
  const [mnfNo,        setMnfNo]        = useState("");
  const [mnfDate,      setMnfDate]      = useState(todayISO());
  const [mnfTypeUI,    setMnfTypeUI]    = useState("regular");
  const [mnfOrigin,    setMnfOrigin]    = useState("");   // place_id (int as string)
  const [mnfDest,      setMnfDest]      = useState("");   // place_id (int as string)
  const [mnfVehicle,   setMnfVehicle]   = useState("");
  const [mnfDriver,    setMnfDriver]    = useState("");
  const [mnfDriverMob, setMnfDriverMob] = useState("");
  const [mnfStatus,    setMnfStatus]    = useState("D"); // D | O | C
  const [mnfViewTitle, setMnfViewTitle] = useState("New Manifest");
  const [mnfViewSub,   setMnfViewSub]   = useState("Fill master details, scan consignments and save");

  // ── scan state
  const [scanValue,      setScanValue]      = useState("");
  const [scanStatusType, setScanStatusType] = useState("idle");
  const [scanStatusMsg,  setScanStatusMsg]  = useState("Ready");
  const [scanError,      setScanError]      = useState(false);
  const [scanLoading,    setScanLoading]    = useState(false);
  const [fetchedRecord,  setFetchedRecord]  = useState(null);
  const [scanRemark,     setScanRemark]     = useState("");

  // ── scanned detail rows
  const [currentScanned, setCurrentScanned] = useState([]);

  // ── saving / closing
  const [saving, setSaving] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);

  // ── view-only mode (true when manifest is Closed and opened for viewing)
  const [viewOnly, setViewOnly] = useState(false);

  // ── toasts
  const [toasts, setToasts] = useState([]);

  const scanInputRef = useRef(null);
  const dateInputRef  = useRef(null);
  const mnfNoInputRef  = useRef(null);
//to generte manifest number according to branch and global wise
  const [numberType, setNumberType] = useState("G");
const [branchPrefix, setBranchPrefix] = useState("");

  // ─────────────────────────────────────────
  // TOAST HELPER
  // ─────────────────────────────────────────
  const toast = useCallback((msg, type = "info") => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, msg, type, fading: false }]);
    setTimeout(() => {
      setToasts((prev) => prev.map((t) => (t.id === id ? { ...t, fading: true } : t)));
      setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 320);
    }, 3000);
  }, []);

  // ─────────────────────────────────────────
  // ESCAPE KEY
  // ─────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        clearScan();
        setFetchedRecord(null);
        setScanRemark("");
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // ─────────────────────────────────────────
  // ON MOUNT: load places dropdown + manifest list
  // ─────────────────────────────────────────
  useEffect(() => {
    loadPlaces();
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─────────────────────────────────────────
  // API: LOAD PLACES DROPDOWN
  // GET /dropdown?mode=plc  → [{ place_id, place_Name }]
  // ─────────────────────────────────────────
  const loadPlaces = async () => {
    try {
      const response = await api.get("/dropdown?mode=plc");
      setPlaces(response.data || []);
    } catch (err) {
      console.error("Failed to load places:", err);
      toast("Could not load branch list", "warn");
    }
  };

  // Helper: get place name by id (for display)
  const placeName = (id) => {
    const p = places.find((x) => String(x.place_id) === String(id));
    return p ? p.place_Name : id;
  };
  const placePin = (id) => {
    const p = places.find((x) => String(x.place_id) === String(id));
    return p ? p.place_Name : "";
  };

  // ─────────────────────────────────────────
  // API: FETCH MANIFEST LIST
  // GET /manifest/list?fromDate=&toDate=&origin=&type=&manifestNumber=
  // ─────────────────────────────────────────
  const fetchList = async (filters = {}) => {
    setListLoading(true);
    try {
      const params = {};
      if (filters.fromDate       || fFrom)  params.fromDate       = filters.fromDate       ?? fFrom;
      if (filters.toDate         || fTo)    params.toDate         = filters.toDate         ?? fTo;
      if (filters.origin)                   params.origin         = filters.origin;
      if (filters.type)                     params.type           = filters.type;
      if (filters.manifestNumber || fMnfNo) params.manifestNumber = filters.manifestNumber ?? fMnfNo;

      const response = await api.get("/api/manifest/list", { params });
      setFilteredList(response.data || []);
      setListPage(1); // reset to first page on every fresh fetch/filter
    } catch (err) {
      console.error("fetchList error:", err);
      toast("Failed to load manifest list", "err");
    } finally {
      setListLoading(false);
    }
  };

  // ─────────────────────────────────────────
  // API: FETCH CONSIGNMENT FROM Cons_Booking
  // GET /manifest/fetch-consignment/{consignmentNo}
  // → { success, data: { Consignment_No, Consignor_Name, ... } }
  // ─────────────────────────────────────────

   const fetchConsignment = async () => {
    if (mnfStatus === "C") { toast("Manifest is closed", "warn"); return; }
    if (!mnfOrigin || !mnfDest) { toast("Select Origin and Destination first", "warn"); return; }

    const raw = scanValue.trim();
    if (!raw) { toast("Enter or scan a Consignment Number", "warn"); return; }

    const cnNo = parseInt(raw, 10);
    if (isNaN(cnNo)) { toast("Consignment Number must be numeric", "warn"); return; }

    // Duplicate guard
    if (currentScanned.find((s) => s.consignmentNo === cnNo)) {
      setScanStatusType("dup");
      setScanStatusMsg("Already added");
      alert(`Consignment ${cnNo} is already added to this manifest.`, "warn");
      // toast(`Consignment ${cnNo} is already in this manifest`, "warn");
      return;
    }

    setScanLoading(true);
    setScanStatusType("scanning");
    setScanStatusMsg("Fetching…");
    setScanError(false);

    try {
      const response = await api.get(`/api/manifest/fetch-consignment/${cnNo}`);
      const data = response.data;

      if (data?.success) {
        const b = data.data;
        setFetchedRecord({
          consignmentNo: b.consignment_No,
          refNo:         b.ref_No          ?? "",
          consignor:     b.consignor_Name  ?? "",
          consignee:     b.consignee_Name  ?? "",
          pcs:           b.no_Pkgs         ?? 0,
          actWt:         b.weight          ?? 0,
          chgWt:         b.weight          ?? 0,  // user can edit in preview
          dimension:     b.dimension       ?? "",                        // not in Cons_Booking — user fills
          jobId:         b.job_ID          ?? null,
          jobName:       b.job_Name        ?? null,
          remark:        b.remarks         ?? "",
          // bookPlace:     b.book_Place      ?? "",
          // delPlace:      b.delivery_Place  ?? "",
          bookPlace: b.book_Place_Name  ?? b.book_Place      ?? "",
          delPlace:  b.delivery_Place_Name ?? b.delivery_Place  ?? "",
        });
        setScanStatusType("ok");
        setScanStatusMsg("Found ✓");
      } else {
        setScanStatusType("err");
        setScanStatusMsg("Not found");
        setScanError(true);
        alert(data?.message || `Consignment ${cnNo} not found`, "err");
        // toast(data?.message || `Consignment ${cnNo} not found`, "err");
        setTimeout(() => setScanError(false), 1500);
      }
    } catch (err) {
      const msg = err.response?.data?.message || `Consignment ${cnNo} not found`;
      setScanStatusType("err");
      setScanStatusMsg("Not found");
      setScanError(true);
      alert(msg, "err");
      // toast(msg, "err");
      setTimeout(() => setScanError(false), 1500);
    } finally {
      setScanLoading(false);
    }
  };


  const addToManifest = () => {
    if (!fetchedRecord) return;
    const now = new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
    setCurrentScanned((prev) => [
      ...prev,
      { ...fetchedRecord, remark: scanRemark || fetchedRecord.remark, scanTime: now },
    ]);
    if (mnfStatus === "D") setMnfStatus("O");
    toast(`Consignment ${fetchedRecord.consignmentNo} added`, "ok");
    clearFetched();
  };

  // ─────────────────────────────────────────
  // API: SAVE MANIFEST (master + details)
  // ── PRINT ──────────────────────────────────────────────────────────────
  const handlePrint = async (overrideData) => {
    // overrideData lets a caller (e.g. saveManifest) print the data that was
    // just saved, before clearManifestForm() resets the on-screen form.
    const scanned           = overrideData?.scanned      ?? currentScanned;
    const printMnfNo        = overrideData?.mnfNo        ?? mnfNo;
    const printMnfDate      = overrideData?.mnfDate      ?? mnfDate;
    const printMnfTypeUI    = overrideData?.mnfTypeUI    ?? mnfTypeUI;
    const printMnfDriver    = overrideData?.mnfDriver    ?? mnfDriver;
    const printMnfDriverMob = overrideData?.mnfDriverMob ?? mnfDriverMob;
    const printMnfVehicle   = overrideData?.mnfVehicle   ?? mnfVehicle;
    const printMnfOrigin    = overrideData?.mnfOrigin    ?? mnfOrigin;
    const printMnfDest      = overrideData?.mnfDest      ?? mnfDest;

    if (!scanned.length) { toast("No consignments to print", "warn"); return; }

    // Fetch company details
    let companyName    = "";
    let companyAddress = "";
    try {
      const res   = await api.get("/api/manifest/company-detail");
      companyName    = res.data.company?.company_Name    || res.data.company?.Company_Name    || "";
      companyAddress = res.data.company?.company_Address || res.data.company?.Company_Address || "";
    } catch { /* proceed without company info */ }

    // Build table rows HTML
    const rowsHtml = scanned.map((r, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${r.consignmentNo ?? ""}</td>
        <td>${r.consignor    ?? ""}</td>
        <td>${r.consignee    ?? ""}</td>
        <td style="text-align:center">${r.pcs ?? ""}</td>
        <td style="text-align:right">${parseFloat(r.actWt || 0).toFixed(2)}</td>
        <td style="text-align:right">${parseFloat(r.chgWt || 0).toFixed(2)}</td>
        <td>${r.dimension ?? "—"}</td>
        <td>${r.jobName   ?? "—"}</td>
        <td>${r.remark    ?? ""}</td>
      </tr>`
    ).join("");

    const totalPcs = scanned.reduce((s, r) => s + (r.pcs  || 0), 0);
    const totalWt  = scanned.reduce((s, r) => s + (parseFloat(r.actWt) || 0), 0);

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Manifest Outscan — ${printMnfNo}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 11px; color: #111; }

    /* ── Company Header ── */
    .company-header {
      background: #1e2d40;
      color: #fff;
      text-align: center;
      padding: 10px 16px;
    }
    .company-header .co-name {
      font-size: 16px;
      font-weight: 700;
      letter-spacing: .5px;
      text-transform: uppercase;
    }
    .company-header .co-addr {
      font-size: 9px;
      color: #bcc8d8;
      margin-top: 3px;
    }

    /* ── Report Heading ── */
    .report-heading {
      text-align: center;
      padding: 6px 12px;
      font-size: 13px;
      font-weight: 700;
      letter-spacing: .4px;
      text-transform: uppercase;
      border: 1px solid #ccc;
      border-top: none;
      background: #f5f7fa;
      color: #1e2d40;
    }

    /* ── Manifest Info Grid ── */
    .info-section {
      border: 1px solid #ccc;
      border-top: none;
      padding: 8px 12px;
    }
    .info-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 6px 12px;
    }
    .info-cell { display: flex; flex-direction: column; }
    .info-cell .lbl {
      font-size: 8px;
      font-weight: 700;
      color: #666;
      text-transform: uppercase;
      letter-spacing: .4px;
    }
    .info-cell .val {
      font-size: 11px;
      font-weight: 600;
      color: #111;
      margin-top: 1px;
    }

    /* ── Table ── */
    .tbl-section { margin-top: 8px; }
    table { width: 100%; border-collapse: collapse; }
    thead tr { background: #1e2d40; color: #fff; }
    thead th {
      padding: 5px 6px;
      font-size: 9px;
      font-weight: 700;
      text-align: left;
      text-transform: uppercase;
      letter-spacing: .3px;
      border: 1px solid #3a4f66;
    }
    tbody tr:nth-child(even) { background: #f5f7fa; }
    tbody td {
      padding: 4px 6px;
      border: 1px solid #ddd;
      font-size: 10px;
      vertical-align: middle;
    }
    tfoot td {
      padding: 5px 6px;
      font-weight: 700;
      font-size: 10px;
      background: #e8edf3;
      border: 1px solid #ccc;
    }

    /* ── Print Button Bar (hidden when printing) ── */
    .print-bar {
      display: flex;
      justify-content: flex-end;
      padding: 8px 12px;
      background: #f0f2f5;
      border-bottom: 1px solid #ddd;
    }
    .print-bar button {
      background: #1e2d40;
      color: #fff;
      border: none;
      padding: 6px 18px;
      font-size: 13px;
      font-weight: 600;
      border-radius: 4px;
      cursor: pointer;
      letter-spacing: .3px;
    }
    .print-bar button:hover { background: #2e4460; }

    @media print {
      .print-bar { display: none; }
      @page { margin: 10mm; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>

  <!-- Print Button Bar -->
  <div class="print-bar">
    <button onclick="window.print()">🖨️ Print</button>
  </div>

  <!-- Section 1: Company Header -->
  <div class="company-header">
    <div class="co-name">${companyName}</div>
    <div class="co-addr">${companyAddress}</div>
  </div>

  <!-- Section 2: Report Heading -->
  <div class="report-heading">Manifest Outscan Report</div>

  <!-- Section 3: Manifest Info -->
  <div class="info-section">
    <div class="info-grid">
      <div class="info-cell">
        <span class="lbl">Manifest Number</span>
        <span class="val">${printMnfNo || "—"}</span>
      </div>
      <div class="info-cell">
        <span class="lbl">Manifest Date</span>
        <span class="val">${printMnfDate || "—"}</span>
      </div>
      <div class="info-cell">
        <span class="lbl">Type</span>
        <span class="val">${printMnfTypeUI ? printMnfTypeUI.charAt(0).toUpperCase() + printMnfTypeUI.slice(1) : "—"}</span>
      </div>
      <div class="info-cell">
        <span class="lbl">Driver</span>
        <span class="val">${printMnfDriver || "—"}</span>
      </div>
      <div class="info-cell">
        <span class="lbl">Driver Mobile</span>
        <span class="val">${printMnfDriverMob || "—"}</span>
      </div>
      <div class="info-cell">
        <span class="lbl">Vehicle No</span>
        <span class="val">${printMnfVehicle || "—"}</span>
      </div>
      <div class="info-cell">
        <span class="lbl">Dispatched From</span>
        <span class="val">${placeName(printMnfOrigin) || "—"}</span>
      </div>
      <div class="info-cell">
        <span class="lbl">Receiving At</span>
        <span class="val">${placeName(printMnfDest) || "—"}</span>
      </div>
      <div class="info-cell">
        <span class="lbl">Total Consignments</span>
        <span class="val">${scanned.length}</span>
      </div>
    </div>
  </div>

  <!-- Section 4: Consignment Table -->
  <div class="tbl-section">
    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Consignment No</th>
          <th>Consignor</th>
          <th>Consignee</th>
          <th style="text-align:center">Pcs</th>
          <th style="text-align:right">Act Wt (kg)</th>
          <th style="text-align:right">Chg Wt (kg)</th>
          <th>Dimension</th>
          <th>Job</th>
          <th>Remark</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml}
      </tbody>
      <tfoot>
        <tr>
          <td colspan="4">TOTAL</td>
          <td style="text-align:center">${totalPcs}</td>
          <td style="text-align:right">${totalWt.toFixed(2)}</td>
          <td colspan="4"></td>
        </tr>
      </tfoot>
    </table>
  </div>

</body>
</html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url  = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };


  // ── ENTER KEY NAVIGATION ─────────────────────────────────────────────────
  // Moves focus to the next visible focusable element when Enter is pressed.
  // Matches the same pattern used in ConsignmentBooking.jsx.
  // Includes: input, select, textarea, btn-primary, btn-green, btn-teal, btn-amber
  // Also includes scan-fetch button (.btn-fetch) and Add Manifest button (.btn-add-manifest)
  const moveFocusToNext = useCallback((currentTarget) => {
    const focusableSelector = [
      'input:not([disabled]):not([readonly])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      'button.btn-primary',
      'button.btn-green',
      'button.btn-teal',
      'button.btn-amber',
      'button.btn-fetch',
      'button.btn-add-manifest',
    ].join(', ');
    const elements = Array.from(document.querySelectorAll(focusableSelector)).filter(el => {
      const rect = el.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });
    const index = elements.indexOf(currentTarget);
    if (index > -1 && index < elements.length - 1) {
      elements[index + 1].focus();
    }
  }, []);

  const handleGlobalKeyDown = useCallback((e) => {
    if (e.key !== "Enter") return;
    // Textarea: allow normal newline
    if (e.target.tagName === "TEXTAREA") return;
    // Buttons: let default click fire
    if (e.target.tagName === "BUTTON") return;
    e.preventDefault();
    moveFocusToNext(e.target);
  }, [moveFocusToNext]);

  // ── PRINT FROM LIST ─────────────────────────────────────────────────────
  // Called from the Preview column in the filter/list table.
  // Fetches full manifest details by ID, then renders the same print format.
  const handlePrintFromList = async (manifestId, m) => {
    // Fetch company info and full manifest details in parallel
    let companyName    = "";
    let companyAddress = "";
    let details        = [];

    try {
      const [compRes, mnfRes] = await Promise.all([
        api.get("/api/manifest/company-detail"),
        api.get(`/api/manifest/${manifestId}`),
      ]);

      companyName    = compRes.data.company?.company_Name    || compRes.data.company?.Company_Name    || "";
      companyAddress = compRes.data.company?.company_Address || compRes.data.company?.Company_Address || "";
      details        = mnfRes.data.details ?? [];
    } catch (err) {
      toast("Failed to load manifest for print", "err");
      return;
    }

    const rowsHtml = details.map((d, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${d.consignment_No ?? ""}</td>
        <td>${d.consignor     ?? ""}</td>
        <td>${d.consignee     ?? ""}</td>
        <td style="text-align:center">${d.pcs ?? ""}</td>
        <td style="text-align:right">${parseFloat(d.act_Wt || 0).toFixed(2)}</td>
        <td style="text-align:right">${parseFloat(d.chg_Wt || 0).toFixed(2)}</td>
        <td>${d.dimension ?? "—"}</td>
        <td>${d.job_Name  ?? "—"}</td>
        <td>${d.remark    ?? ""}</td>
      </tr>`
    ).join("");

    const totalPcs = details.reduce((s, d) => s + (d.pcs  || 0), 0);
    const totalWt  = details.reduce((s, d) => s + (parseFloat(d.act_Wt) || 0), 0);

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Manifest Outscan — ${m.manifest_Number}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 11px; color: #111; }
    .company-header { background: #1e2d40; color: #fff; text-align: center; padding: 10px 16px; }
    .company-header .co-name { font-size: 16px; font-weight: 700; letter-spacing: .5px; text-transform: uppercase; }
    .company-header .co-addr { font-size: 9px; color: #bcc8d8; margin-top: 3px; }
    .report-heading { text-align: center; padding: 6px 12px; font-size: 13px; font-weight: 700; letter-spacing: .4px; text-transform: uppercase; border: 1px solid #ccc; border-top: none; background: #f5f7fa; color: #1e2d40; }
    .info-section { border: 1px solid #ccc; border-top: none; padding: 8px 12px; }
    .info-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px 12px; }
    .info-cell { display: flex; flex-direction: column; }
    .info-cell .lbl { font-size: 8px; font-weight: 700; color: #666; text-transform: uppercase; letter-spacing: .4px; }
    .info-cell .val { font-size: 11px; font-weight: 600; color: #111; margin-top: 1px; }
    .tbl-section { margin-top: 8px; }
    table { width: 100%; border-collapse: collapse; }
    thead tr { background: #1e2d40; color: #fff; }
    thead th { padding: 5px 6px; font-size: 9px; font-weight: 700; text-align: left; text-transform: uppercase; letter-spacing: .3px; border: 1px solid #3a4f66; }
    tbody tr:nth-child(even) { background: #f5f7fa; }
    tbody td { padding: 4px 6px; border: 1px solid #ddd; font-size: 10px; vertical-align: middle; }
    tfoot td { padding: 5px 6px; font-weight: 700; font-size: 10px; background: #e8edf3; border: 1px solid #ccc; }
    .print-bar { display: flex; justify-content: flex-end; padding: 8px 12px; background: #f0f2f5; border-bottom: 1px solid #ddd; }
    .print-bar button { background: #1e2d40; color: #fff; border: none; padding: 6px 18px; font-size: 13px; font-weight: 600; border-radius: 4px; cursor: pointer; }
    .print-bar button:hover { background: #2e4460; }
    @media print {
      .print-bar { display: none; }
      @page { margin: 10mm; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="print-bar"><button onclick="window.print()">🖨️ Print</button></div>
  <div class="company-header">
    <div class="co-name">${companyName}</div>
    <div class="co-addr">${companyAddress}</div>
  </div>
  <div class="report-heading">Manifest Outscan Report</div>
  <div class="info-section">
    <div class="info-grid">
      <div class="info-cell"><span class="lbl">Manifest Number</span><span class="val">${m.manifest_Number || "—"}</span></div>
      <div class="info-cell"><span class="lbl">Manifest Date</span><span class="val">${m.manifest_Date?.slice(0,10) || "—"}</span></div>
      <div class="info-cell"><span class="lbl">Type</span><span class="val">${TYPE_LABEL[m.type ?? ""] ?? m.type ?? "—"}</span></div>
      <div class="info-cell"><span class="lbl">Driver</span><span class="val">${m.driver_Name || "—"}</span></div>
      <div class="info-cell"><span class="lbl">Driver Mobile</span><span class="val">${m.driver_No || "—"}</span></div>
      <div class="info-cell"><span class="lbl">Vehicle No</span><span class="val">${m.vehical_No || "—"}</span></div>
      <div class="info-cell"><span class="lbl">Dispatched From</span><span class="val">${placeName(m.origin) || "—"}</span></div>
      <div class="info-cell"><span class="lbl">Receiving At</span><span class="val">${placeName(m.destination) || "—"}</span></div>
      <div class="info-cell"><span class="lbl">Total Consignments</span><span class="val">${details.length}</span></div>
    </div>
  </div>
  <div class="tbl-section">
    <table>
      <thead>
        <tr>
          <th>#</th><th>Consignment No</th><th>Consignor</th><th>Consignee</th>
          <th style="text-align:center">Pcs</th>
          <th style="text-align:right">Act Wt (kg)</th>
          <th style="text-align:right">Chg Wt (kg)</th>
          <th>Dimension</th><th>Job</th><th>Remark</th>
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
      <tfoot>
        <tr>
          <td colspan="4">TOTAL</td>
          <td style="text-align:center">${totalPcs}</td>
          <td style="text-align:right">${totalWt.toFixed(2)}</td>
          <td colspan="4"></td>
        </tr>
      </tfoot>
    </table>
  </div>
</body>
</html>`;

    const blob = new Blob([html], { type: "text/html" });
    const url  = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  // POST /manifest/save
  // Body: { master: {...}, details: [{...}] }
  // → { success, manifest_Id, manifestNumber }
  // ─────────────────────────────────────────
  const saveManifest = async () => {
    if (!mnfOrigin || !mnfDest) { toast("Select Origin and Destination", "warn"); return; }
    if (currentScanned.length === 0) { toast("Add at least one consignment", "warn"); return; }

    setSaving(true);

    // Capture GPS co-ordinates — proceed even if browser denies permission
    let latitude  = null;
    let longitude = null;
    try {
      const pos = await new Promise((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej, { timeout: 3000 })
      );
      latitude  = pos.coords.latitude;
      longitude = pos.coords.longitude;
    } catch { /* GPS unavailable — stored as null */ }

    try {
      const body = {
        master: {
          Manifest_Id:          manifestId,               // 0 = new insert
          Manifest_Number:      mnfNo,                    // server re-generates for new
          Manifest_Date:        mnfDate,
          Type:                 TYPE_CODE[mnfTypeUI] ?? "R",
          Vehical_No:           mnfVehicle,
          Origin:               parseInt(mnfOrigin, 10),  // int — place_id
          Destination:          parseInt(mnfDest, 10),    // int — place_id
          Driver_Name:          mnfDriver,
          Driver_No:            mnfDriverMob,
          Total_Consignments:   currentScanned.length,
          Total_Pcs:            currentScanned.reduce((a, r) => a + (r.pcs ?? 0), 0),
          Total_Act_Wgt:        currentScanned.reduce((a, r) => a + (parseFloat(r.actWt) || 0), 0),
          Total_Chrg_Wgt:       currentScanned.reduce((a, r) => a + (parseFloat(r.chgWt) || 0), 0),
          Company_Code:         1,   // TODO: pull from auth/session context
          FinYear_Code:         1,   // TODO: pull from auth/session context
          // Created_By:           1,   // TODO: pull from auth/session context
          NumberingType:        numberType,
          BranchPrefix:         branchPrefix,
          Latitude:             latitude,
          Longitude:            longitude,
          PC_Name:              navigator.userAgent,      // browser/device identifier
        },
        details: currentScanned.map((r) => ({
          Consignment_No: r.consignmentNo,
          Consignor:      r.consignor,
          Consignee:      r.consignee,
          Pcs:            r.pcs,
          Act_Wt:         r.actWt,
          Chg_Wt:         r.chgWt,
          Dimension:      r.dimension || "",
          Job_Id:         r.jobId     ?? null,
          Remark:         r.remark    || "",
        })),
      };

      const response = await api.post("/api/manifest/save", body);
      const data = response.data;

      if (data?.success) {
        // Always use the number returned by the server — it is built from
        // the real DB-generated Manifest_Id so it is guaranteed correct.
        const isUpdate = manifestId > 0;

        // Snapshot the just-saved data BEFORE clearManifestForm() wipes the
        // form state, so the print-out reflects what was actually saved.
        const printSnapshot = {
          scanned:      currentScanned,
          mnfNo:        data.manifestNumber,
          mnfDate,
          mnfTypeUI,
          mnfDriver,
          mnfDriverMob,
          mnfVehicle,
          mnfOrigin,
          mnfDest,
        };

        setManifestId(data.manifest_Id);
        setMnfNo(data.manifestNumber);
        // setMnfStatus("O");
        setMnfViewTitle(`Manifest ${data.manifestNumber}`);
        toast(`Manifest ${data.manifestNumber} ${isUpdate ? "updated" : "saved"} successfully`, "ok");

        // 1) Show the save-success alert first.
        alert(isUpdate
          ? `Manifest Updated Successfully: ${data.manifestNumber}`
          : `Manifest Outscan Save Successfully: ${data.manifestNumber}`);

        // 2) Then ask whether to print, and only print if the user says yes.
        if (window.confirm("Do you want to print the manifest outscan?")) {
          await handlePrint(printSnapshot);
        }

        clearManifestForm();
        fetchList();
      } else {
        toast(data?.message || "Save failed", "err");
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Save failed";
      toast(msg, "err");
      console.error("saveManifest error:", err);
    } finally {
      setSaving(false);
    }
  };

  //rest form after save successfully
  const clearManifestForm = () => {
  // setManifestId(0);
  // setMnfNo("Will be assigned on Save");
  setMnfDate(todayISO());
  setMnfTypeUI("regular");
  setMnfOrigin("");
  setMnfDest("");
  setMnfVehicle("");
  setMnfDriver("");
  setMnfDriverMob("");
  setMnfStatus("D");

  setCurrentScanned([]);
  setFetchedRecord(null);

  setScanValue("");
  setScanStatusType("idle");
  setScanStatusMsg("Ready");
  setScanError(false);
  setScanRemark("");

  setMnfViewTitle("New Manifest");
  // setMnfViewSub("Fill master details, scan consignments and save");

  setTimeout(() => scanInputRef.current?.focus(), 200);
};
  // ─────────────────────────────────────────
  // API: CLOSE MANIFEST
  // POST /manifest/close/{manifestId}
  // → { success, message }
  // ─────────────────────────────────────────
  const confirmClose = async () => {
    // If not saved yet — save first then close
    let idToClose = manifestId;
    if (manifestId === 0) {
      await saveManifest();
      // saveManifest sets manifestId via setManifestId; read ref value below
      idToClose = manifestId; // will be updated after re-render; use a workaround
    }

    // After saveManifest resolves, manifestId state may not have updated yet.
    // We handle this by re-reading from the response above OR by checking the
    // current saved manifestId from state inside a functional update.
    setManifestId((currentId) => {
      const finalId = currentId > 0 ? currentId : idToClose;

      api.post(`/api/manifest/close/${finalId}`)
        .then((response) => {
          if (response.data?.success) {
            setMnfStatus("C");
            setShowCloseModal(false);
            toast("Manifest closed successfully", "ok");
            fetchList();
          } else {
            toast(response.data?.message || "Close failed", "err");
          }
        })
        .catch((err) => {
          const msg = err.response?.data?.message || "Close failed";
          toast(msg, "err");
          console.error("confirmClose error:", err);
        });

      return finalId; // return same id, no state change
    });
  };

  // ─────────────────────────────────────────
  // API: OPEN EXISTING MANIFEST
  // GET /manifest/{id}
  // → { master: {...}, details: [{...}] }
  // ─────────────────────────────────────────
  const openManifest = async (id) => {
    try {
      const response = await api.get(`/api/manifest/${id}`);
      const data = response.data;

      const m = data.master;
      setManifestId(m.manifest_Id);
      setMnfNo(m.manifest_Number ?? "");
      setMnfDate(m.manifest_Date ? m.manifest_Date.slice(0, 10) : todayISO());
      setMnfTypeUI(TYPE_UI[m.type] ?? "regular");
      setMnfOrigin(String(m.origin      ?? ""));
      setMnfDest(String(m.destination   ?? ""));
      setMnfVehicle(m.vehicle_No        ?? "");
      setMnfDriver(m.driver_Name        ?? "");
      setMnfDriverMob(m.driver_No       ?? "");
      setMnfStatus(m.status             ?? "O");
      setViewOnly(m.status === "C");    // closed manifests open in view-only mode
      setMnfViewTitle(`Manifest ${m.manifest_Number}`);
      setMnfViewSub(
        `${placeName(m.origin)} → ${placeName(m.destination)} · ${m.manifest_Date?.slice(0, 10) ?? ""}`
      );

      setCurrentScanned(
        (data.details ?? []).map((d) => ({
          consignmentNo: d.consignment_No,
          refNo:         d.bookingRef  ?? "",
          consignor:     d.consignor   ?? "",
          consignee:     d.consignee   ?? "",
          pcs:           d.pcs         ?? 0,
          actWt:         d.act_Wt      ?? 0,
          chgWt:         d.chg_Wt      ?? 0,
          dimension:     d.dimension   ?? "",
          jobId:         d.job_Id      ?? null,
          jobName:       d.job_Name    ?? null,
          remark:        d.remark      ?? "",
          scanTime:      "",
        }))
      );

      setFetchedRecord(null);
      setScanValue("");
      setScanStatusType("idle");
      setScanStatusMsg("Ready");
      setView("manifest");

      if (m.Status !== "C") setTimeout(() => scanInputRef.current?.focus(), 250);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to load manifest";
      toast(msg, "err");
      console.error("openManifest error:", err);
    }
  };

  // ─────────────────────────────────────────
  // OPEN NEW MANIFEST
  // ─────────────────────────────────────────
  const openNewManifest = () => {
    setManifestId(0);
    // Show a pending label — the REAL number comes back from /save response.
    // We no longer pre-generate because the server assigns the number AFTER
    // the DB INSERT (uses the auto-generated Manifest_Id), so any pre-fetched
    // number would be off-by-one when another save happens in between.
    setMnfNo("Will be assigned on Save");
    setMnfDate(todayISO());
    setMnfTypeUI("regular");
    setMnfOrigin("");
    setMnfDest("");
    setMnfVehicle("");
    setMnfDriver("");
    setMnfDriverMob("");
    setMnfStatus("D");
    setViewOnly(false);
    setCurrentScanned([]);
    setFetchedRecord(null);
    setScanValue("");
    setScanStatusType("idle");
    setScanStatusMsg("Ready");
    setMnfViewTitle("New Manifest");
    // setMnfViewSub("Fill master details, scan consignments and save");
    setView("manifest");
    setTimeout(() => mnfNoInputRef.current?.focus(), 100);
  };

  // ─────────────────────────────────────────
  // API: RE-OPEN A CLOSED MANIFEST
  // POST /manifest/open/{manifestId}
  // → { success, message }
  // ─────────────────────────────────────────
  const reopenManifest = async () => {
    try {
      const response = await api.post(`/api/manifest/open/${manifestId}`);
      if (response.data?.success) {
        setMnfStatus("O");
        setViewOnly(false);
        toast("Manifest re-opened successfully", "ok");
        fetchList();
      } else {
        toast(response.data?.message || "Re-open failed", "err");
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Re-open failed";
      toast(msg, "err");
    }
  };

  // ─────────────────────────────────────────
  // FILTER HELPERS
  // ─────────────────────────────────────────
  const applyFilters = () => {
    fetchList({
      fromDate:       fFrom,
      toDate:         fTo,
      origin:         fBranch,
      type:           fType ? TYPE_CODE[fType] : "",
      manifestNumber: fMnfNo,
    });
  };

  const clearFilters = () => {
    setFBranch("");
    setFType("");
    setFFrom(todayISO().slice(0, 7) + "-01");
    setFTo(todayISO());
    setFMnfNo("");
    fetchList({});
  };

  // ─────────────────────────────────────────
  // SCAN HELPERS
  // ─────────────────────────────────────────
  const clearScan = () => {
    setScanValue("");
    setScanStatusType("idle");
    setScanStatusMsg("Ready");
    setScanError(false);
    scanInputRef.current?.focus();
  };

  const clearFetched = () => {
    setFetchedRecord(null);
    setScanRemark("");
    clearScan();
  };

  const removeScanned = (idx) => {
    setCurrentScanned((prev) => prev.filter((_, i) => i !== idx));
    toast("Entry removed", "info");
  };

  const clearAllScanned = () => {
    if (!currentScanned.length) return;
    if (!window.confirm(`Remove all ${currentScanned.length} entries?`)) return;
    setCurrentScanned([]);
  };

  const goToList = () => {
    setView("list");
    fetchList();
  };

  // ─────────────────────────────────────────
  // COMPUTED TOTALS
  // ─────────────────────────────────────────
  const totals = {
    cnt: currentScanned.length,
    pcs: currentScanned.reduce((a, r) => a + (r.pcs ?? 0), 0),
    act: currentScanned.reduce((a, r) => a + (parseFloat(r.actWt) || 0), 0),
    chg: currentScanned.reduce((a, r) => a + (parseFloat(r.chgWt) || 0), 0),
  };

  const listTotals = {
    cnt: filteredList.reduce((a, m) => a + (m.consignmentCount ?? 0), 0),
    pcs: filteredList.reduce((a, m) => a + (m.totalPcs         ?? 0), 0),
    act: filteredList.reduce((a, m) => a + (m.totalActWt       ?? 0), 0),
    chg: filteredList.reduce((a, m) => a + (m.totalChgWt       ?? 0), 0),
  };

  // ── Manifest Register: sort by Manifest No. descending (newest first)
  const sortedList = [...filteredList].sort((a, b) =>
    String(b.manifest_Number ?? "").localeCompare(
      String(a.manifest_Number ?? ""),
      undefined,
      { numeric: true, sensitivity: "base" }
    )
  );

  // ── Manifest Register: pagination
  const listTotalPages = Math.max(1, Math.ceil(sortedList.length / listPageSize));
  const listPageSafe    = Math.min(listPage, listTotalPages);
  const listPageStart   = (listPageSafe - 1) * listPageSize;
  const paginatedList   = sortedList.slice(listPageStart, listPageStart + listPageSize);

  const isClosed     = mnfStatus === "C";
  const routeVisible = mnfOrigin && mnfDest;

  const statusPillCls = { D: "sp sp-draft", O: "sp sp-open", C: "sp sp-closed" };
  const statusPillTxt = { D: "● Draft",     O: "● Open",     C: "● Closed" };

  const SCAN_ICONS = {
    idle:     <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>,
    scanning: <path d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" />,
    ok:       <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />,
    err:      <><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>,
    dup:      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />,
  };
  const SCAN_STATUS_CLS = { idle: "ssb-idle", scanning: "ssb-scanning", ok: "ssb-ok", err: "ssb-err", dup: "ssb-dup" };

  const modalSummary = {
    route: `${placeName(mnfOrigin)} → ${placeName(mnfDest)}`,
    cnt:   `${totals.cnt} consignment${totals.cnt !== 1 ? "s" : ""}`,
    pcs:   `${totals.pcs} packages`,
    act:   `${totals.act.toFixed(2)} kg`,
    chg:   `${totals.chg.toFixed(2)} kg`,
  };

  // ═════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════
  return (
    <div className="mos-root" onKeyDown={handleGlobalKeyDown}>
      <div className="page">

        {/* ── BREADCRUMB ── */}
        {/* <div className="breadcrumb">
          <span>Operations</span>
          <span className="bc-sep">›</span>
          <span>Manifest</span>
          <span className="bc-sep">›</span>
          <span className="bc-cur">
            {view === "list" ? "Out Scan" : mnfNo || "New Manifest"}
          </span>
        </div> */}

        {/* ══════════════════════════════════════
            LIST VIEW
        ══════════════════════════════════════ */}
        {view === "list" && (
          <div className="list-view">

            {/* Title + New button */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", margin: "14px 0 16px", flexWrap: "wrap", gap: 12 }}>
              <div>
                <h1 style={{ fontSize: 18, fontWeight: 700, color: "var(--navy)" }}>Manifest Out Scan</h1>
                <p style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 3 }}>Manage and track all outward manifests from your branch</p>
              </div>
              {rights.add && (
              <button className="btn btn-primary btn-lg" onClick={openNewManifest}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
                New Manifest
              </button>)}
            </div>

            {/* ── FILTER CARD ── */}
            <div className="filter-card">
              <div className="filter-band">
                <div className="filter-band-title">🔍 Filter Manifests</div>
                <button
                  style={{ background: "rgba(255,255,255,.12)", color: "rgba(255,255,255,.85)", border: "1px solid rgba(255,255,255,.2)", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                  onClick={clearFilters}
                >Clear</button>
              </div>
              <div className="filter-body">
                <div className="mo-filter-grid">
                  <div className="field">
                    <label>Branch</label>
                    <PlaceSelect
                      value={fBranch}
                      onChange={(e) => setFBranch(e.target.value)}
                      options={[{ place_id: "", place_Name: "All Branches" }, ...places]}
                      placeholder="All Branches"
                    />
                  </div>
                  <div className="field">
                    <label>Manifest Type</label>
                    <ComboBox
                      value={fType}
                      onChange={(e) => setFType(e.target.value)}
                      options={[
                        { id: "", name: "All Types" },
                        { id: "regular", name: "Regular" },
                        { id: "express", name: "Express" },
                        { id: "od", name: "ODA / Special" },
                      ]}
                      placeholder="All Types"
                      valueKey="id" labelKey="name"
                    />
                  </div>
                  <div className="field">
                    <label>From Date</label>
                    <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} />
                  </div>
                  <div className="field">
                    <label>To Date</label>
                    <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} />
                  </div>
                  <div className="field">
                    <label>Manifest No.</label>
                    <input
                      type="text"
                      value={fMnfNo}
                      onChange={(e) => setFMnfNo(e.target.value.toUpperCase())}
                      placeholder="e.g. MNF2606…"
                    />
                  </div>
                </div>
                <div className="filter-actions">
                  <button className="btn btn-outline btn-sm" onClick={clearFilters}>Reset</button>
                  <button className="btn btn-primary btn-sm" onClick={applyFilters}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" /></svg>
                    Search
                  </button>
                </div>
              </div>
            </div>

            {/* ── LIST TABLE ── */}
            <div className="list-card">
              <div className="list-hdr-band">
                <div className="list-hdr-title">📋 Manifest Register</div>
                <div className="list-hdr-r">
                  <span className="list-count-badge">{filteredList.length} records</span>
                  {rights.export && (<button
                    style={{ background: "rgba(255,255,255,.1)", color: "rgba(255,255,255,.85)", border: "1px solid rgba(255,255,255,.2)", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}
                    onClick={() => toast("Exporting…", "ok")}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                    Export
                  </button>)}
                </div>
              </div>

              <div className="tbl-wrap">
                {listLoading ? (
                  <div style={{ padding: 48, textAlign: "center", color: "var(--muted)" }}>Loading manifests…</div>
                ) : (
                  <table className="list-table">
                    <thead>
                      <tr>
                        <th className="center" style={{ width: 38 }}>#</th>
                        <th>Manifest No.</th>
                        <th>Type</th>
                        <th>Origin</th>
                        <th>Destination</th>
                        <th className="center">CNR Count</th>
                        <th className="center">Total Pcs</th>
                        <th className="right">Act Wt (kg)</th>
                        <th className="right">Chg Wt (kg)</th>
                        <th>Date</th>
                        {/* <th className="center">Status</th> */}
                        <th className="center">Actions</th>
                        <th className="center">Preview</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedList.length === 0 ? (
                        <tr>
                          <td colSpan="13">
                            <div className="empty-state">
                              <div className="empty-icon">📋</div>
                              <div className="empty-title">No manifests found</div>
                              <div className="empty-sub">Create a new manifest or adjust filters</div>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        paginatedList.map((m, i) => {
                          const st      = m.status ?? "O";
                          const pillCls = st === "C" ? "pill-closed" : st === "O" ? "pill-open" : "pill-draft";
                          const pillTxt = STATUS_LABEL[st] ?? st;
                          const tCode   = m.type ?? "";
                          return (
                            <tr key={m.manifest_Id} style={{ cursor: "pointer" }} onClick={() => openManifest(m.manifest_Id)}>
                              <td className="center" style={{ color: "var(--muted)", fontSize: 12 }}>{listPageStart + i + 1}</td>
                              <td>
                                <span className="mnf-no-link">{m.manifest_Number}</span>
                              </td>
                              <td>
                                <span style={{ background: "var(--blue-lt)", color: "var(--blue)", fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 4 }}>
                                  {TYPE_LABEL[tCode] ?? tCode}
                                </span>
                              </td>
                              <td style={{ fontSize: 12.5 }}>
                                <strong style={{ color: "var(--navy)" }}>{placeName(m.origin)}</strong>
                              </td>
                              <td style={{ fontSize: 12.5 }}>
                                <strong style={{ color: "var(--navy)" }}>{placeName(m.destination)}</strong>
                              </td>
                              <td className="center"><strong style={{ color: "var(--blue)", fontSize: 14 }}>{m.consignmentCount ?? m.Total_Consignments ?? 0}</strong></td>
                              <td className="center"><strong>{m.totalPcs ?? m.Total_Pcs ?? 0}</strong></td>
                              <td className="right"><strong>{(m.totalActWt ?? m.Total_Act_Wgt ?? 0).toFixed(2)}</strong></td>
                              <td className="right" style={{ color: "var(--teal)", fontWeight: 700 }}>{(m.totalChgWt ?? m.Total_Chrg_Wgt ?? 0).toFixed(2)}</td>
                              <td style={{ color: "var(--slate)" }}>{m.manifest_Date?.slice(0, 10) ?? ""}</td>
                              {/* <td className="center"><span className={`pill ${pillCls}`}>{pillTxt}</span></td> */}
                              <td className="center">
                                <button
                                  className="icon-btn view"
                                  onClick={(e) => { e.stopPropagation(); openManifest(m.manifest_Id); }}
                                  title="View"
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                                </button>
                                {/* {(m.status ?? "O") !== "C" && ( */}
                                {rights.edit && m.status !== "C" && (
                                  <button
                                    className="icon-btn edit"
                                    onClick={(e) => { e.stopPropagation(); openManifest(m.manifest_Id); }}
                                    title="Edit"
                                    style={{ marginLeft: 4 }}
                                  >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>
                                  </button>
                                )}
                              </td>
                              <td className="center">
                                {rights.export &&( <button
                                  className="icon-btn"
                                  title="Print Preview"
                                  style={{ color: "var(--teal)" }}
                                  onClick={(e) => { e.stopPropagation(); handlePrintFromList(m.manifest_Id, m); }}
                                >
                                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
                                </button>)}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                )}
              </div>

              {filteredList.length > 0 && (
                <div className="list-footer">
                  <span>Showing {listPageStart + 1}–{Math.min(listPageStart + listPageSize, filteredList.length)} of {filteredList.length} manifests</span>
                  <div className="list-pagination-controls">
                    <button type="button" className="lp-nav-btn" disabled={listPageSafe <= 1}
                      onClick={() => setListPage((p) => Math.max(1, p - 1))}>
                      ‹ Prev
                    </button>
                    <span className="lp-page-indicator">Page {listPageSafe} of {listTotalPages}</span>
                    <button type="button" className="lp-nav-btn" disabled={listPageSafe >= listTotalPages}
                      onClick={() => setListPage((p) => Math.min(listTotalPages, p + 1))}>
                      Next ›
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ══════════════════════════════════════
            MANIFEST FORM VIEW
        ══════════════════════════════════════ */}
        {view === "manifest" && (
          <div className="mnf-view show">

            {/* Print header (hidden on screen) */}
            <div className="print-header">
              <h2>Saami Tradestar Logistics Pvt Ltd</h2>
              <p>Manifest Out — {mnfNo} | {modalSummary.route} | {mnfDate}</p>
            </div>

            {/* Action bar */}
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", margin: "14px 0 16px", flexWrap: "wrap", gap: 12 }}>
              <div>
                <h1 style={{ fontSize: 18, fontWeight: 700, color: "var(--navy)" }}>{mnfViewTitle}</h1>
                {/* <p style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 3 }}>{mnfViewSub}</p> */}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button className="btn btn-outline btn-sm" onClick={goToList}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
                  Back to List
                </button>
                {rights.export && manifestId > 0 && (<button className="btn btn-red btn-sm" onClick={handlePrint}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" /><rect x="6" y="14" width="12" height="8" /></svg>
                  Print
                </button>)}
                {rights.add && rights.edit && (<button className="btn btn-navy btn-sm" onClick={saveManifest} disabled={saving || isClosed}>
                  {saving ? (manifestId > 0 ? "Updating…" : "Saving…") : (
                    <>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v14a2 2 0 01-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>
                      {manifestId > 0 ? "Update" : "Save"}
                    </>
                  )}
                </button>)}
                {/* {!isClosed && (
                  <button className="btn btn-primary btn-lg" onClick={() => setShowCloseModal(true)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    Close Manifest
                  </button>
                )}
                {isClosed && (
                  <button className="btn btn-green btn-lg" onClick={reopenManifest}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14l-4-4 1.41-1.41L10 13.17l6.59-6.59L18 8l-8 8z" /></svg>
                    Open Manifest
                  </button>
                )} */}
              </div>
            </div>

            {/* ── MANIFEST MASTER CARD ── */}
            <div className="mnf-master-card">
              <div className="mnf-master-band">
                <div className="mmb-l">
                  <div className="mmb-icon">
                    <svg viewBox="0 0 24 24" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                  </div>
                  <div>
                    <div className="mmb-title">Manifest Master</div>
                    <div className="mmb-sub">Set origin, destination and vehicle info</div>
                  </div>
                </div>
                {/* <span className={statusPillCls[mnfStatus]}>{statusPillTxt[mnfStatus]}</span> */}
              </div>
              <div className="mnf-master-body">
                <div className="master-grid">

                  {/* ── Manifest No. — auto-generated, read-only, shown BEFORE save ── */}
                  <div className="field">
                    <label>Manifest No.</label>
                    <div style={{ position: "relative" }}>
                      <input
                        ref={mnfNoInputRef}
                        type="text"
                        value={mnfNo}
                        readOnly
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); dateInputRef.current?.focus(); } }}
                        style={{
                          fontWeight: 800,
                          letterSpacing: 1.4,
                          fontSize: 14,
                          color: "var(--navy)",
                          background: "#eef2ff",
                          border: "2px solid var(--blue)",
                          borderRadius: 6,
                          padding: "8px 36px 8px 11px",
                          width: "100%",
                          cursor: "default",
                        }}
                      />

                    </div>
                    <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 3 }}>
                      {manifestId === 0 ? "Auto-assigned after Save (MNF + YYMM + ID)" : `DB ID: ${manifestId}`}
                    </div>
                  </div>


                  <div className="field">
                    <label>Date <span style={{ color: "var(--red)" }}>*</span></label>
                    <input ref={dateInputRef} type="date" value={mnfDate} onChange={(e) => !viewOnly && setMnfDate(e.target.value)} readOnly={viewOnly} style={viewOnly ? { background: "#f5f5f5", cursor: "default" } : {}} />
                  </div>

                  <div className="field">
                    <label>Type</label>
                    <ComboBox
                      value={mnfTypeUI}
                      onChange={(e) => !viewOnly && setMnfTypeUI(e.target.value)}
                      options={[
                        { id: "regular", name: "Regular" },
                        { id: "express", name: "Express" },
                        { id: "od", name: "ODA / Special" },
                      ]}
                      placeholder="Select type…"
                      disabled={viewOnly}
                      valueKey="id" labelKey="name"
                    />
                  </div>

                  <div className="field">
                    <label>Vehicle / LR No.</label>
                    <input type="text" value={mnfVehicle} onChange={(e) => !viewOnly && setMnfVehicle(e.target.value)} placeholder="e.g. MH02-AB-1234" readOnly={viewOnly} style={viewOnly ? { background: "#f5f5f5", cursor: "default" } : {}} />
                  </div>

                  {/* ── Origin — from API places dropdown ── */}
                  <div className="field">
                    <label>Origin <span style={{ color: "var(--red)" }}>*</span></label>
                    <PlaceSelect
                      value={mnfOrigin}
                      onChange={(e) => !viewOnly && setMnfOrigin(e.target.value)}
                      options={places}
                      placeholder="— Select Origin —"
                      disabled={viewOnly}
                    />
                  </div>

                  {/* ── Destination — from API places dropdown ── */}
                  <div className="field">
                    <label>Destination <span style={{ color: "var(--red)" }}>*</span></label>
                    <PlaceSelect
                      value={mnfDest}
                      onChange={(e) => !viewOnly && setMnfDest(e.target.value)}
                      options={places.filter((p) => String(p.place_id) !== String(mnfOrigin))}
                      placeholder="— Select Destination —"
                      disabled={viewOnly}
                    />
                  </div>

                  <div className="field">
                    <label>Driver Name</label>
                    <input type="text" value={mnfDriver} onChange={(e) => !viewOnly && setMnfDriver(e.target.value)} placeholder="Driver name" readOnly={viewOnly} style={viewOnly ? { background: "#f5f5f5", cursor: "default" } : {}} />
                  </div>

                  <div className="field">
                    <label>Driver Mobile</label>  
                    <input type="text" value={mnfDriverMob} 
                    onChange={(e) => !viewOnly && setMnfDriverMob(e.target.value.replace(/\D/g, ""))}
                    onKeyDown={(e) => {
                      if (["e", "E", "+", "-", "."].includes(e.key)) e.preventDefault();
                    }}
                    placeholder="10-digit mobile" maxLength={10} readOnly={viewOnly} inputMode="numeric"
                    style={viewOnly ? { background: "#f5f5f5", cursor: "default" } : {}} />
                  </div>

                </div>

                {/* Route strip — shown when both origin & dest selected */}
                {routeVisible && (
                  <div className="route-display">
                    <div>
                      <div className="rp-label">Origin</div>
                      <div className="rp-name">{placeName(mnfOrigin)}</div>
                      <div className="rp-code">PIN: {placePin(mnfOrigin)}</div>
                    </div>
                    <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}>
                      <div className="ra-line"></div>
                      <span className="ra-truck">🚚</span>
                      <div className="ra-line"></div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div className="rp-label">Destination</div>
                      <div className="rp-name">{placeName(mnfDest)}</div>
                      <div className="rp-code">PIN: {placePin(mnfDest)}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ── TOTALS STRIP ── */}
            {/* <div className="totals-strip">
              <div className="tot-box blue"><div className="tot-lbl">Consignments</div><div className="tot-val">{totals.cnt}</div><div className="tot-unit">scanned</div></div>
              <div className="tot-box teal"><div className="tot-lbl">Total Pieces</div><div className="tot-val">{totals.pcs}</div><div className="tot-unit">packages</div></div>
              <div className="tot-box green"><div className="tot-lbl">Actual Weight</div><div className="tot-val">{totals.act.toFixed(2)}</div><div className="tot-unit">kg</div></div>
              <div className="tot-box navy"><div className="tot-lbl">Charged Weight</div><div className="tot-val">{totals.chg.toFixed(2)}</div><div className="tot-unit">kg (billing)</div></div>
            </div> */}

            {/* ── SCAN PANEL ── */}
            <div className="scan-panel" style={isClosed ? { opacity: 0.55, pointerEvents: "none" } : {}}>
              <div className="scan-band">
                <div className="scan-band-l">
                  <div className="scan-band-title">📡 Consignment Scan</div>
                  <div className="scan-band-sub">Enter consignment number → Fetch → Add to Manifest</div>
                </div>
                <div className="scan-summary-chips">
                  <div className="sc-chip"><div className="sc-val">{totals.cnt}</div><div className="sc-lbl">CNR</div></div>
                  <div className="sc-chip"><div className="sc-val">{totals.pcs}</div><div className="sc-lbl">Pcs</div></div>
                  <div className="sc-chip"><div className="sc-val">{totals.chg.toFixed(1)}</div><div className="sc-lbl">Kg (Chg)</div></div>
                </div>
              </div>
              <div className="scan-body">
                <div className="scan-input-row">
                  <div className="scan-field">
                    <label>Consignment Number (CNR)</label>
                    <div className={`scan-input-wrap${scanError ? " error" : ""}`}>
                      <span className="scan-icon">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 5v4M3 5h4M3 19v-4M3 19h4M21 5v4M21 5h-4M21 19v-4M21 19h-4M8 5v14M12 5v14M16 5v14" />
                        </svg>
                      </span>
                      <input
                        ref={scanInputRef}
                        type="number"
                        className="scan-input"
                        value={scanValue}
                        onChange={(e) => setScanValue(e.target.value)}
                        // onKeyDown={(e) => e.key === "Enter" && fetchConsignment()}
                        onKeyDown={(e) => {
                          if (e.key === "e" || e.key === "E") e.preventDefault();
                          if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
                          if (e.key === "Enter") fetchConsignment();
                        }}
                        onWheel={(e) => e.target.blur()}
                        placeholder="Enter numeric CNR…"
                        autoComplete="off"
                        disabled={isClosed}
                      />
                      {scanValue && (
                        <button className="scan-clear" onClick={clearScan}>×</button>
                      )}
                    </div>
                    <div className="scan-kbd">
                      Press <span className="kbd">Enter</span> to fetch &nbsp;|&nbsp; <span className="kbd">Esc</span> to clear
                    </div>
                  </div>

                  <div className={`scan-status-badge ${SCAN_STATUS_CLS[scanStatusType] || "ssb-idle"}`}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="15" height="15">
                      {SCAN_ICONS[scanStatusType]}
                    </svg>
                    <span>{scanStatusMsg}</span>
                  </div>

                  <button
                    className="btn btn-primary btn-fetch"
                    onClick={fetchConsignment}
                    style={{ alignSelf: "flex-end", padding: "11px 20px" }}
                    disabled={isClosed || scanLoading}
                  >
                    {scanLoading ? "Fetching…" : (
                      <>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="15" height="15"><path d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z" /></svg>
                        Fetch
                      </>
                    )}
                  </button>
                </div>

                {/* ── FETCHED PREVIEW (from Cons_Booking) ── */}
                {fetchedRecord && (
                  <div className="fetched-preview">
                    <div className="fp-top">
                      <div>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--muted)", marginBottom: 2 }}>
                          Consignment found in Cons_Booking
                        </div>
                        <div className="fp-cnr">#{fetchedRecord.consignmentNo}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        {fetchedRecord.refNo && <span className="job-tag">Ref: {fetchedRecord.refNo}</span>}
                      </div>
                    </div>
                    <div className="fp-grid">
                      <div><div className="fp-cell-lbl">Consignor</div><div className="fp-cell-val">{fetchedRecord.consignor}</div></div>
                      <div><div className="fp-cell-lbl">Consignee</div><div className="fp-cell-val">{fetchedRecord.consignee}</div></div>
                      <div><div className="fp-cell-lbl">📦 Pieces</div><div className="fp-cell-val blue">{fetchedRecord.pcs} pcs</div></div>
                      <div><div className="fp-cell-lbl">⚖️ Actual Wt</div><div className="fp-cell-val">{fetchedRecord.actWt} kg</div></div>
                      <div><div className="fp-cell-lbl">📍 Book Place</div><div className="fp-cell-val teal">{fetchedRecord.bookPlace}</div></div>
                      <div><div className="fp-cell-lbl">📍 Del. Place</div><div className="fp-cell-val green">{fetchedRecord.delPlace}</div></div>
                    </div>

                    {/* Editable fields before adding */}
                    <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
                      <div className="field" style={{ flex: 1, minWidth: 160 }}>
                        <label>Dimension (L×W×H cm)</label>
                        <input
                          type="text"
                          value={fetchedRecord.dimension}
                          onChange={(e) => setFetchedRecord((p) => ({ ...p, dimension: e.target.value }))}
                          placeholder="e.g. 60×40×35"
                          readOnly
                        />
                      </div>
                      <div className="field" style={{ flex: 1, minWidth: 140 }}>
                        <label>Charged Weight (kg)</label>
                        <input
                          type="number"
                          step="0.01"
                          value={fetchedRecord.chgWt}
                          onChange={(e) => setFetchedRecord((p) => ({ ...p, chgWt: parseFloat(e.target.value) || 0 }))}
                          readOnly
                        />
                      </div>
                      <div className="field" style={{ flex: 1, minWidth: 160 }}>
                        <label>Remark</label>
                        <input
                          type="text"
                          value={scanRemark}
                          onChange={(e) => setScanRemark(e.target.value)}
                          placeholder="Optional remark…"
                        />
                      </div>
                      <button className="btn btn-green btn-add-manifest" onClick={addToManifest}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13"><path d="M12 5v14M5 12h14" /></svg>
                        Add to Manifest
                      </button>
                      <button className="btn btn-outline" onClick={clearFetched}>Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ── SCANNED CONSIGNMENTS TABLE (Manifest_Detail) ── */}
            <div className="scanned-card">
              <div className="scanned-hdr">
                <div className="scanned-hdr-title">📋 Scanned Consignments — Manifest Detail</div>
                <div className="scanned-hdr-r">
                  <span className="scanned-count">{currentScanned.length} entr{currentScanned.length === 1 ? "y" : "ies"}</span>
                  {!isClosed && (
                    <button
                      style={{ color: "rgba(255,255,255,.8)", border: "1px solid rgba(255,255,255,.2)", background: "rgba(255,255,255,.1)", borderRadius: 6, padding: "5px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                      onClick={clearAllScanned}
                    >
                      Clear All
                    </button>
                  )}
                </div>
              </div>
              <div className="tbl-wrap">
                <table className="manifest-table">
                  <thead>
                    <tr>
                      <th className="center" style={{ width: 38 }}>#</th>
                      <th>Consignment No.</th>
                      <th>Consignor</th>
                      <th>Consignee</th>
                      <th className="center">Pcs</th>
                      <th className="right">Act Wt (kg)</th>
                      <th className="right">Chg Wt (kg)</th>
                      <th className="center">Dimension</th>
                      <th>Job</th>
                      <th>Remark</th>
                      <th style={{ width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentScanned.length === 0 ? (
                      <tr>
                        <td colSpan="11">
                          <div className="empty-state">
                            <div className="empty-icon">📡</div>
                            <div className="empty-title">No consignments added yet</div>
                            <div className="empty-sub">Enter a Consignment Number above and click Fetch</div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      currentScanned.map((r, i) => (
                        <tr key={`${r.consignmentNo}-${i}`}>
                          <td className="center"><span className="sn-badge">{i + 1}</span></td>
                          <td>
                            <span className="sn-col">{r.consignmentNo}</span>
                            {r.refNo    && <div style={{ fontSize: 10.5, color: "var(--muted)", marginTop: 2 }}>Ref: {r.refNo}</div>}
                            {r.scanTime && <div style={{ fontSize: 10.5, color: "var(--muted)" }}>{r.scanTime}</div>}
                          </td>
                          <td style={{ fontSize: 12.5 }}>{r.consignor}</td>
                          <td style={{ fontSize: 12.5 }}>{r.consignee}</td>
                          <td className="center"><strong style={{ color: "var(--blue)", fontSize: 14 }}>{r.pcs}</strong></td>
                          <td className="right"><strong>{parseFloat(r.actWt).toFixed(2)}</strong></td>
                          <td className="right" style={{ color: "var(--teal)", fontWeight: 700 }}>{parseFloat(r.chgWt).toFixed(2)}</td>
                          <td className="center">
                            {r.dimension
                              ? <span className="dim-tag">{r.dimension}</span>
                              : <span style={{ color: "var(--muted)", fontSize: 11 }}>—</span>}
                          </td>
                          <td>
                            {r.jobName
                              ? <span className="job-tag">{r.jobName}</span>
                              : r.jobId
                                ? <span className="job-tag">Job #{r.jobId}</span>
                                : <span style={{ color: "var(--muted)", fontSize: 11 }}>—</span>}
                          </td>
                          <td style={{ fontSize: 12, color: "var(--slate)" }}>{r.remark || "—"}</td>
                          <td className="center">
                            {!isClosed && (
                              <button className="del-btn" onClick={() => removeScanned(i)} title="Remove">✕</button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  {currentScanned.length > 0 && (
                    <tfoot>
                      <tr className="row-subtotal">
                        <td colSpan="4" style={{ padding: "10px 12px" }}>SUB TOTAL</td>
                        <td className="center" style={{ fontSize: 14 }}>{totals.pcs}</td>
                        <td className="right" style={{ fontSize: 14 }}>{totals.act.toFixed(2)} kg</td>
                        <td className="right" style={{ fontSize: 14 }}>{totals.chg.toFixed(2)} kg</td>
                        <td colSpan="4"></td>
                      </tr>
                      <tr className="row-grand">
                        <td colSpan="4" style={{ padding: "10px 12px" }}>
                          GRAND TOTAL —{" "}
                          <span style={{ color: "rgba(255,255,255,.7)", fontSize: 12, fontWeight: 600 }}>
                            {currentScanned.length} consignment{currentScanned.length !== 1 ? "s" : ""}
                          </span>
                        </td>
                        <td className="center" style={{ fontSize: 15 }}>{totals.pcs}</td>
                        <td className="right" style={{ fontSize: 15 }}>{totals.act.toFixed(2)} kg</td>
                        <td className="right" style={{ fontSize: 15 }}>{totals.chg.toFixed(2)} kg</td>
                        <td colSpan="4"></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
             
              {/* ── TOTALS STRIP ── */}
            <div className="totals-strip">
              <div className="tot-box blue"><div className="tot-lbl">Consignments</div><div className="tot-val">{totals.cnt}</div><div className="tot-unit">scanned</div></div>
              <div className="tot-box teal"><div className="tot-lbl">Total Pieces</div><div className="tot-val">{totals.pcs}</div><div className="tot-unit">packages</div></div>
              <div className="tot-box green"><div className="tot-lbl">Actual Weight</div><div className="tot-val">{totals.act.toFixed(2)}</div><div className="tot-unit">kg</div></div>
              <div className="tot-box navy"><div className="tot-lbl">Charged Weight</div><div className="tot-val">{totals.chg.toFixed(2)}</div><div className="tot-unit">kg (billing)</div></div>
            </div>            
          </div>
        )}
      </div>

      {/* ══ CLOSE MANIFEST MODAL ══ */}
      {showCloseModal && (
        <div className="overlay" onClick={(e) => e.target === e.currentTarget && setShowCloseModal(false)}>
          <div className="modal">
            <div className="modal-hd">
              <h3>Close Manifest</h3>
              <button className="modal-close" onClick={() => setShowCloseModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13, color: "var(--slate)", marginBottom: 14, lineHeight: 1.7 }}>
                Closing manifest <strong style={{ color: "var(--navy)" }}>{mnfNo}</strong>.
                Once closed, no further consignments can be added.
              </p>
              <div className="modal-details-box">
                <div className="modal-row"><span>Manifest No.</span><span>{mnfNo}</span></div>
                <div className="modal-row"><span>Route</span><span>{modalSummary.route}</span></div>
                <div className="modal-row"><span>Consignments</span><span>{modalSummary.cnt}</span></div>
                <div className="modal-row"><span>Total Pieces</span><span>{modalSummary.pcs}</span></div>
                <div className="modal-row"><span>Actual Weight</span><span>{modalSummary.act}</span></div>
                <div className="modal-row" style={{ borderTop: "2px solid var(--border)", marginTop: 4, paddingTop: 8 }}>
                  <span style={{ fontWeight: 700, color: "var(--teal)", fontSize: 13.5 }}>Charged Weight</span>
                  <span style={{ fontWeight: 800, color: "var(--teal)", fontSize: 16 }}>{modalSummary.chg}</span>
                </div>
              </div>
              {manifestId === 0 && (
                <div style={{ marginTop: 10, background: "var(--amber-lt)", border: "1px solid rgba(217,119,6,.25)", borderRadius: 6, padding: "10px 12px", fontSize: 12.5, color: "var(--amber)" }}>
                  ⚠ Manifest not saved yet — it will be saved and closed in one step.
                </div>
              )}
            </div>
            <div className="modal-ft">
              <button className="btn btn-outline" onClick={() => setShowCloseModal(false)}>Cancel</button>
              <button className="btn btn-green" onClick={confirmClose}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Confirm &amp; Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══ TOASTS ══ */}
      <ToastContainer toasts={toasts} />
    </div>
  );
}